import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarDays,
  ChartNoAxesColumn,
  Flag,
  IndianRupee,
  LayoutDashboard,
  LogOut,
  Search,
  Settings,
  Users,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { api } from '../lib/api.js';
import { fmtWeekdayDayMonth, fmtPhone, phoneDigits } from '@siva/shared';
import { greeting } from '../lib/greeting.js';
import { ScrollToTop } from '../lib/ScrollToTop.js';
import { t } from '../i18n/index.js';
import { useAuth } from '../lib/auth.js';
import { HealthBadge } from '../lib/HealthBadge.js';
import { Skeleton, Avatar } from '../lib/components.js';
import { ThemeToggle } from '../lib/ThemeToggle.js';
import { AuthShell } from '../lib/AuthShell.js';
import { NotificationBell } from './NotificationBell.js';
import { BrandLogo, BrandMark } from '../lib/Brand.js';
import { useOverlayCount } from '../lib/overlay.js';
import { AdminLoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordResetPages.js';
import { EnrolPage } from './pages/EnrolPage.js';
import { WorkersPage } from './pages/WorkersPage.js';
import { AdminsPage } from './pages/AdminsPage.js';
import { SystemPage } from './pages/SystemPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { FlagsPage } from './pages/FlagsPage.js';
import { ReportsPage } from './pages/ReportsPage.js';
import { PaymentsPage } from './pages/PaymentsPage.js';
import { WeeklyOffPage } from './pages/WeeklyOffPage.js';
import { AttendancePage } from './pages/AttendancePage.js';
import { WorkerDetailPage } from './pages/WorkerDetailPage.js';
import { PhotosProvider, usePhotos } from './photos.js';

type NavItem = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: number;
  /** Desktop only: the phone's bottom bar has room for seven. */
  wideOnly?: boolean;
  /** The badge shows on the phone only (where the item it belongs to is not in the bar). */
  badgeOnPhoneOnly?: boolean;
};

function SideLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      className={`side-link${item.wideOnly ? ' wide-only' : ''}`}
      aria-label={item.label}
    >
      <Icon size={22} aria-hidden="true" />
      <span className="side-label" aria-hidden="true">
        {item.label}
      </span>
      <span className="tip" aria-hidden="true">
        {item.label}
      </span>
      {item.badge ? (
        <span className={`nav-badge${item.badgeOnPhoneOnly ? ' phone-only' : ''}`}>{item.badge}</span>
      ) : null}
    </NavLink>
  );
}

/**
 * Icon rail. It is expanded in exactly two situations: the pointer is over it, or one of its
 * links holds keyboard focus. Everything else collapses it — leaving with the pointer, clicking
 * anything inside it, navigating, and any overlay opening on top of the page (a menu, the
 * notification panel, a modal, a slide-over), because a pointer that was over the rail when the
 * overlay appeared will never send the leave event that would otherwise close it.
 *
 * The state is held here rather than in CSS :hover so that those last cases can reach it. No
 * timers are involved: every transition is driven by an event or by a render.
 */
function Sidebar({ items }: { items: NavItem[] }) {
  const location = useLocation();
  const [hovering, setHovering] = useState(false);
  const [focused, setFocused] = useState(false);
  const overlays = useOverlayCount();

  // Navigating collapses the rail even though the pointer has not moved.
  useEffect(() => {
    setHovering(false);
    setFocused(false);
  }, [location.pathname, location.search]);

  const open = (hovering || focused) && overlays === 0;

  return (
    <nav
      className={`sidebar${open ? ' open' : ''}`}
      aria-label="Main"
      data-open={open ? 'true' : 'false'}
      onPointerEnter={(e) => {
        // Touch and pen "enter" on tap and never leave; only a mouse hovers.
        if (e.pointerType === 'mouse') setHovering(true);
      }}
      onPointerLeave={() => setHovering(false)}
      onClick={(e) => {
        setHovering(false);
        setFocused(false);
        (e.target as HTMLElement).closest<HTMLElement>('a, button')?.blur();
      }}
      onFocus={(e) => {
        // Only a real keyboard focus expands it; a click focuses the link too, and the click
        // handler above has already decided to collapse.
        if (e.target.matches(':focus-visible')) setFocused(true);
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocused(false);
      }}
    >
      <span className="brand" aria-hidden="true">
        <BrandMark size={40} className="brand-collapsed" />
        <BrandLogo className="brand-expanded" height={36} />
      </span>
      {items.map((it) => (
        <SideLink key={it.to} item={it} />
      ))}
      <span className="spacer" />
    </nav>
  );
}

function WorkerSearch() {
  const [q, setQ] = useState('');
  const [all, setAll] = useState<{ id: string; name: string; phone: string | null }[] | null>(null);
  const navigate = useNavigate();
  const { photoOf } = usePhotos();
  useEffect(() => {
    if (q.length < 1 || all) return;
    api<{ workers: { id: string; name: string; phone: string | null }[] }>('/admin/workers')
      .then((r) => setAll(r.workers))
      .catch(() => setAll([]));
  }, [q, all]);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || !all) return [];
    const digits = phoneDigits(s);
    return all
      .filter(
        (w) =>
          w.name.toLowerCase().includes(s) ||
          (digits.length >= 3 && phoneDigits(w.phone ?? '').includes(digits)),
      )
      .slice(0, 8);
  }, [q, all]);
  return (
    <div className="search" role="search">
      <Search size={16} className="search-icon" aria-hidden="true" />
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('search_workers')}
        aria-label={t('search_workers')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && hits[0]) {
            navigate(`/workers/${hits[0].id}`);
            setQ('');
          }
          if (e.key === 'Escape') setQ('');
        }}
      />
      {hits.length > 0 && (
        <div className="results">
          {hits.map((w) => (
            <Link key={w.id} to={`/workers/${w.id}`} onClick={() => setQ('')}>
              <Avatar name={w.name} size={26} photoUrl={photoOf(w.id)} />
              <span>{w.name}</span>
              <small className="muted">{fmtPhone(w.phone)}</small>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminApp() {
  const { me, logout } = useAuth();
  const signedIn = !!me?.user && me.session?.kind === 'admin' && me.session.mfa_verified;
  const [openFlags, setOpenFlags] = useState<number>(0);
  useEffect(() => {
    if (!signedIn) return;
    const poll = () =>
      api<{ open_count: number }>('/admin/flags/count')
        .then((r) => setOpenFlags(r.open_count))
        .catch(() => {});
    poll();
    const id = setInterval(poll, 60_000);
    return () => clearInterval(id);
  }, [signedIn]);

  const items: NavItem[] = [
    { to: '/', label: t('nav_attendance'), icon: LayoutDashboard, end: true },
    { to: '/workers', label: t('nav_workers'), icon: Users },
    { to: '/weekly-off', label: t('nav_weekly_off'), icon: CalendarDays },
    { to: '/reports', label: t('nav_reports'), icon: ChartNoAxesColumn },
    { to: '/payments', label: t('nav_payments'), icon: IndianRupee },
    { to: '/settings', label: t('nav_settings'), icon: Settings },
    // Flags left the phone's bar on 19 September 2026: eight tabs did not fit, and the owner chose
    // to reach them from the Admin page, where the open count rides on the Admin tab. The desktop
    // sidebar has the room, and the approved design (15 October) lists Flags there.
    { to: '/flags', label: t('nav_flags'), icon: Flag, badge: openFlags, wideOnly: true },
    {
      to: '/admin',
      label: t('nav_system'),
      icon: ShieldCheck,
      badge: openFlags,
      badgeOnPhoneOnly: true,
    },
  ];
  const today = fmtWeekdayDayMonth(new Date());

  if (!signedIn) {
    return (
      <AuthShell subtitle={t('auth_admin_console')}>
        {!me ? (
          <Skeleton lines={4} />
        ) : (
          <Routes>
            <Route path="/enrol/:token" element={<EnrolPage />} />
            <Route path="/forgot" element={<ForgotPasswordPage />} />
            <Route path="/reset/:token" element={<ResetPasswordPage />} />
            <Route path="*" element={<AdminLoginPage />} />
          </Routes>
        )}
      </AuthShell>
    );
  }

  return (
    <PhotosProvider>
      <div className="shell admin">
        <Sidebar items={items} />
        <header className="topbar">
          <div className="greeting">
            <h1>
              {greeting()}, {me?.user?.name}
            </h1>
            <div className="date">{today}</div>
          </div>
          <span className="grow" />
          <WorkerSearch />
          <NotificationBell />
          <ThemeToggle />
          <HealthBadge />
          <button
            type="button"
            className="user-chip"
            onClick={() => void logout()}
            title={t('sign_out')}
          >
            <Avatar name={me?.user?.name ?? '?'} size={28} />
            <span className="sr-only">{t('sign_out')} · </span>
            <span>{me?.user?.name}</span>
            <LogOut size={14} aria-hidden="true" />
          </button>
        </header>
        <ScrollToTop />
        <main>
          <Routes>
            <Route path="/" element={<AttendancePage />} />
            <Route path="/workers" element={<WorkersPage />} />
            <Route path="/workers/:id" element={<WorkerDetailPage />} />
            <Route path="/weekly-off" element={<WeeklyOffPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/payments" element={<PaymentsPage />} />
            <Route path="/flags" element={<FlagsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admins" element={<AdminsPage />} />
            <Route path="/admin" element={<SystemPage />} />
            <Route path="/system" element={<Navigate to="/admin" replace />} />
            <Route path="/enrol/:token" element={<EnrolPage />} />
          </Routes>
        </main>
      </div>
    </PhotosProvider>
  );
}
